import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, FileTypes, PhotoDimensions } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { PhotoCamera2 } from '../../../../components/PhotoCamera2';
import { RetakePhoto, TakePhoto } from '../../../../components/buttons/';
import { Uploader } from '../../../../components/Uploader';
import { UploadSquareSolid } from 'iconoir-react';
import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';
import { classNames } from '../../../../style/classNames';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

export const Route = createFileRoute('/_authenticated/$patientId/payment/insuranceInfo')({
  component: HealthInsuranceInfoPage,
});

function HealthInsuranceInfoPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate, mutateAsync } = useUpdateChartSection();

  const [spouseProvides, setSpouseProvides] = React.useState(false);
  const [prefersUpload, setPrefersUpload] = React.useState(false);
  const [takingPhoto, setTakingPhoto] = React.useState({
    healthInsuranceFront: false,
    healthInsuranceBack: false,
  });
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [warned, setWarned] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [photoTwoWorking, setPhotoTwoWorking] = React.useState(false);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType?.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // SET STATE TO TRIGGER EFFECT
  const handleCheckboxClick = () => {
    setSpouseProvides(!spouseProvides);
  };

  // REMOVE PHOTO URI FROM FIRESTORE BEFORE RETAKING PHOTO
  const handleRetakePhoto = (pic: string) => {
    mutate(
      {
        patientId: patientId,
        docData: {
          HEALTH_INSURANCE_INFO: {
            returning: true,
            insured: true,
            insuranceCo: chart?.HEALTH_INSURANCE_INFO?.insuranceCo ? chart.HEALTH_INSURANCE_INFO.insuranceCo : '',
            secondaryInsuranceCo: chart?.HEALTH_INSURANCE_INFO?.secondaryInsuranceCo
              ? chart.HEALTH_INSURANCE_INFO.secondaryInsuranceCo
              : '',
            spouseName: chart?.HEALTH_INSURANCE_INFO?.spouseName ? chart.HEALTH_INSURANCE_INFO.spouseName : '',
            spouseDOB: chart?.HEALTH_INSURANCE_INFO?.spouseDOB ?? null,
            street: chart?.HEALTH_INSURANCE_INFO?.street ? chart.HEALTH_INSURANCE_INFO.street : '',
            city: chart?.HEALTH_INSURANCE_INFO?.city ? chart.HEALTH_INSURANCE_INFO.city : '',
            state: chart?.HEALTH_INSURANCE_INFO?.state ? chart.HEALTH_INSURANCE_INFO.state : '',
            zip: chart?.HEALTH_INSURANCE_INFO?.zip ? chart.HEALTH_INSURANCE_INFO.zip : '',
            country: chart?.HEALTH_INSURANCE_INFO?.country ? chart.HEALTH_INSURANCE_INFO.country : '',
            healthInsuranceCardFrontURI:
              chart.HEALTH_INSURANCE_INFO.healthInsuranceCardFrontURI && pic !== 'healthInsuranceCardFrontURI'
                ? chart.HEALTH_INSURANCE_INFO.healthInsuranceCardFrontURI
                : '',
            healthInsuranceCardBackURI:
              chart.HEALTH_INSURANCE_INFO.healthInsuranceCardBackURI && pic !== 'healthInsuranceCardBackURI'
                ? chart.HEALTH_INSURANCE_INFO.healthInsuranceCardBackURI
                : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // THE FORM DATA TO WRITE — SHARED BY THE SUBMIT AND THE BACKGROUND INIT, SO BOTH WRITE THE SAME
  const healthInsuranceInfoPayload = () => ({
    patientId: patientId,
    docData: {
      HEALTH_INSURANCE_INFO: {
        returning: true,
        insured: true,
        insuranceCo: healthInsuranceForm.state.values.insuranceCo,
        secondaryInsuranceCo: healthInsuranceForm.state.values.secondaryInsuranceCo,
        spouseName: healthInsuranceForm.state.values.spouseName,
        spouseDOB: healthInsuranceForm.state.values.spouseDOB,
        street: healthInsuranceForm.state.values.street,
        city: healthInsuranceForm.state.values.city,
        state: healthInsuranceForm.state.values.state,
        zip: healthInsuranceForm.state.values.zip,
        country: healthInsuranceForm.state.values.country,
        healthInsuranceCardFrontURI: chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardFrontURI
          ? chart.HEALTH_INSURANCE_INFO.healthInsuranceCardFrontURI
          : '',
        healthInsuranceCardBackURI: chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardBackURI
          ? chart.HEALTH_INSURANCE_INFO.healthInsuranceCardBackURI
          : '',
      },
    },
  });

  // WRITE FORM DATA TO FIRESTORE
  const handleHealthInsuranceInfoUpdate = () => {
    mutate(healthInsuranceInfoPayload(), {
      onSuccess: () => {
        // alert('Successfully updated document');
        setIsSubmitted(true);
      },
      onError: () => {
        notifyChartSaveError();
      },
    });
  };

  // INITIALIZE FIRESTORE DOC IF IT DOESN'T EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  useInitChartSection({
    needed: !!chart && !chart.HEALTH_INSURANCE_INFO,
    write: () => mutateAsync(healthInsuranceInfoPayload()).then(() => setIsSubmitted(true)),
  });

  // INITIALIZE FORM
  const healthInsuranceForm = useForm({
    defaultValues: {
      insured: true,
      insuranceCo: chart?.HEALTH_INSURANCE_INFO?.insuranceCo ? chart.HEALTH_INSURANCE_INFO.insuranceCo : '',
      secondaryInsuranceCo: chart?.HEALTH_INSURANCE_INFO?.secondaryInsuranceCo
        ? chart?.HEALTH_INSURANCE_INFO?.secondaryInsuranceCo
        : '',
      spouseName: chart?.HEALTH_INSURANCE_INFO?.spouseName ? chart.HEALTH_INSURANCE_INFO.spouseName : '',
      spouseDOB: chart?.HEALTH_INSURANCE_INFO?.spouseDOB
        ? typeof chart.HEALTH_INSURANCE_INFO.spouseDOB === 'string'
          ? chart.HEALTH_INSURANCE_INFO.spouseDOB
          : chart.HEALTH_INSURANCE_INFO.spouseDOB.toDate().toLocaleDateString('en-US')
        : '',
      street: chart?.HEALTH_INSURANCE_INFO?.street ? chart.HEALTH_INSURANCE_INFO.street : '',
      city: chart?.HEALTH_INSURANCE_INFO?.city ? chart.HEALTH_INSURANCE_INFO.city : '',
      state: chart?.HEALTH_INSURANCE_INFO?.state ? chart.HEALTH_INSURANCE_INFO.state : '',
      zip: chart?.HEALTH_INSURANCE_INFO?.zip ? chart.HEALTH_INSURANCE_INFO.zip : '',
      country: chart?.HEALTH_INSURANCE_INFO?.country ? chart.HEALTH_INSURANCE_INFO.country : '',
      healthInsuranceCardFrontURI: chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardFrontURI
        ? chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardFrontURI
        : '',
      healthInsuranceCardBackURI: chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardBackURI
        ? chart.HEALTH_INSURANCE_INFO.healthInsuranceCardBackURI
        : '',
    },
    onSubmit: async () => {
      await handleHealthInsuranceInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="health-insurance-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="health-insurance-page-header" textContent="Health Insurance Information" />
          <div id="health-insurance-page-main" className="flex w-5/6 flex-row justify-around">
            <div className="flex flex-col items-start">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  healthInsuranceForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <healthInsuranceForm.Field
                    name="insuranceCo"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Health Insurance Co"
                          id="health-insurance-co"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                      </>
                    )}
                  />
                  <healthInsuranceForm.Field
                    name="secondaryInsuranceCo"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Secondary Health Insurance Co"
                          id="health-insurance-secondary-co"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                      </>
                    )}
                  />
                  <Checkbox
                    id="provider-is-spouse-checkbox"
                    label="My primary and/or secondary insurance is provided by my spouse."
                    name="spouseIsProvider"
                    onChange={() => {
                      handleCheckboxClick();
                    }}
                    checked={spouseProvides}
                  />
                  {/* ONLY NECESSARY IF PATIENT IS INSURED THROUGH SPOUSE */}
                  {spouseProvides && (
                    <div>
                      <healthInsuranceForm.Field
                        name="spouseName"
                        children={(field) => (
                          <>
                            <FormInput
                              label="Spouse's Full Name"
                              id="health-insurance-spouse-name"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              required={false}
                            />
                          </>
                        )}
                      />
                      <healthInsuranceForm.Field
                        name="spouseDOB"
                        children={(field) => (
                          <>
                            <FormInput
                              label="Spouse's Date of Birth (MM/DD/YYYY)"
                              id="health-insurance-spouse-dob"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              required={false}
                            />
                          </>
                        )}
                      />
                      <healthInsuranceForm.Field
                        name="street"
                        children={(field) => (
                          <>
                            <FormInput
                              label="Street Address"
                              id="health-insurance-spouse-street"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              required={false}
                            />
                          </>
                        )}
                      />
                      <healthInsuranceForm.Field
                        name="city"
                        children={(field) => (
                          <>
                            <FormInput
                              label="City"
                              id="health-insurance-spouse-city"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              required={false}
                            />
                          </>
                        )}
                      />
                      <healthInsuranceForm.Field
                        name="state"
                        children={(field) => (
                          <>
                            <FormInput
                              label="State"
                              id="health-insurance-spouse-state"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              required={false}
                            />
                          </>
                        )}
                      />
                      <healthInsuranceForm.Field
                        name="zip"
                        children={(field) => (
                          <>
                            <FormInput
                              label="ZIP"
                              id="health-insurance-spouse-zip"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              required={false}
                            />
                          </>
                        )}
                      />
                      <healthInsuranceForm.Field
                        name="country"
                        children={(field) => (
                          <>
                            <FormInput
                              label="Country"
                              id="health-insurance-spouse-country"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              required={false}
                            />
                          </>
                        )}
                      />
                    </div>
                  )}
                </div>
              </form>
              {/* FRONT OF HEALTH INSURANCE CARD */}
              <div id="insurance-front-container" className="my-4">
                <p className="text-xl font-bold">{`Front of Health Insurance Card:`}</p>
                {chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardFrontURI &&
                takingPhoto.healthInsuranceFront === false ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={chart.HEALTH_INSURANCE_INFO?.healthInsuranceCardFrontURI}
                      style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                      alt="Health Insurance Card Front"
                    />
                    <RetakePhoto setter={() => handleRetakePhoto('healthInsuranceCardFrontURI')} />
                  </div>
                ) : (
                  takingPhoto.healthInsuranceFront === false && (
                    <>
                      <div className="flex flex-row">
                        <TakePhoto setter={() => setTakingPhoto({ ...takingPhoto, healthInsuranceFront: true })} />
                        <button
                          onClick={() => {
                            setPrefersUpload(!prefersUpload);
                          }}
                        >
                          <UploadSquareSolid />
                        </button>
                      </div>
                      {prefersUpload && (
                        <Uploader
                          {...{
                            docToUpdate: 'HEALTH_INSURANCE_INFO',
                            bucket: 'patient',
                            type: FileTypes.healthInsuranceFront,
                            title: 'Upload Front of Health Insurance Card',
                            elementId: 'health-insurance-card-front-file-upload',
                          }}
                        />
                      )}
                    </>
                  )
                )}
                {takingPhoto.healthInsuranceFront && (
                  <PhotoCamera2
                    type={FileTypes.healthInsuranceFront}
                    docToUpdate="HEALTH_INSURANCE_INFO"
                    style={'card'}
                    bucket={'patient'}
                    path={`user/${patientId}/images/healthInsuranceCardFront`}
                    patientId={patientId}
                    hideCamera={() => {
                      setPhotoOneWorking(false);
                      setTakingPhoto({ ...takingPhoto, healthInsuranceFront: false });
                    }}
                    working={photoOneWorking}
                    setWorking={setPhotoOneWorking}
                  />
                )}
              </div>
              {/* BACK OF MAIN HEALTH INSURANCE CARD */}
              <div id="insurance-back-container" className="my-4">
                <p className="text-xl font-bold">{`Back of Health Insurance Card:`}</p>
                {chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardBackURI &&
                takingPhoto.healthInsuranceBack === false ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={chart.HEALTH_INSURANCE_INFO?.healthInsuranceCardBackURI}
                      style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                      alt="Health Insurance Card Back"
                    />
                    <RetakePhoto setter={() => handleRetakePhoto('healthInsuranceCardBackURI')} />
                  </div>
                ) : (
                  takingPhoto.healthInsuranceBack === false && (
                    <>
                      <div className="flex flex-row">
                        <TakePhoto setter={() => setTakingPhoto({ ...takingPhoto, healthInsuranceBack: true })} />
                        <button
                          onClick={() => {
                            setPrefersUpload(!prefersUpload);
                          }}
                        >
                          <UploadSquareSolid />
                        </button>
                      </div>
                      {prefersUpload && (
                        <Uploader
                          {...{
                            docToUpdate: 'HEALTH_INSURANCE_INFO',
                            bucket: 'patient',
                            type: FileTypes.healthInsuranceBack,
                            title: 'Upload Back of Health Insurance Card',
                            elementId: 'health-insurance-card-back-file-upload',
                          }}
                        />
                      )}
                    </>
                  )
                )}
                {takingPhoto.healthInsuranceBack && (
                  <PhotoCamera2
                    type={FileTypes.healthInsuranceBack}
                    docToUpdate="HEALTH_INSURANCE_INFO"
                    style={'card'}
                    bucket={'patient'}
                    path={`user/${patientId}/images/healthInsuranceCardBack`}
                    patientId={patientId}
                    hideCamera={() => {
                      setPhotoTwoWorking(false);
                      setTakingPhoto({ ...takingPhoto, healthInsuranceBack: false });
                    }}
                    working={photoTwoWorking}
                    setWorking={setPhotoTwoWorking}
                  />
                )}
              </div>
              <div className="flex w-full flex-col items-center">
                <Button
                  id="submit-health-insurance"
                  type="submit"
                  textContent="Submit"
                  onClick={handleHealthInsuranceInfoUpdate}
                />
              </div>
            </div>
          </div>
          <div id="nav" className="flex flex-row">
            {isSubmitted && (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={
                    !chart?.HEALTH_INSURANCE_INFO?.insuranceCo.length ||
                    !chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardFrontURI.length ||
                    !chart?.HEALTH_INSURANCE_INFO?.healthInsuranceCardBackURI.length
                  }
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
